// redux/slices/dayOffSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ========================= Thunks ========================= */

// Добавление выходного дня (PRIVATE + JWT)
export const addDayOff = createAsyncThunk(
  "dayOff/addDayOff",
  async ({ uid, dateOfAppointment, isDayOffExists }, { rejectWithValue }) => {
    try {
      if (!uid || !dateOfAppointment) throw new Error("UID или дата не переданы");

      const res = await apiRequest("POST", "/day-off", {
        uid,
        dateOfAppointment,
        isDayOffExists,
      });

      if (!res?.data) throw new Error("No response data received from server.");
      return res.data; // ожидается объект dayOff
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }
      return rejectWithValue(
        err?.response?.data ||
          err?.message ||
          "Ошибка при добавлении выходного дня"
      );
    }
  }
);

// Получение выходных дней для одного uid (PRIVATE + JWT)
export const fetchDayOffs = createAsyncThunk(
  "dayOff/fetchDayOffs",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const res = await apiRequest("GET", `/day-off/${uid}`);

      const data = Array.isArray(res?.data) ? res.data : [];
      return data;
    } catch (err) {
      const status = err?.response?.status;

      // 404 — нет записей, это ОК → возвращаем пустой массив как успешный результат
      if (status === 404) {
        return [];
      }

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        err?.response?.data ||
          err?.message ||
          "Ошибка при загрузке выходных дней"
      );
    }
  }
);

// Удаление выходного дня (PRIVATE + JWT)
export const deleteDayOff = createAsyncThunk(
  "dayOff/deleteDayOff",
  async ({ uid, dateOfAppointment }, { rejectWithValue }) => {
    try {
      if (!uid || !dateOfAppointment) {
        throw new Error("UID или дата не переданы");
      }

      const res = await apiRequest(
        "DELETE",
        `/day-off/${uid}/${dateOfAppointment}`
      );

      if (res?.status !== 200 && res?.status !== 204) {
        throw new Error(`Ошибка удаления: ${res?.status}`);
      }

      return dateOfAppointment;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }
      return rejectWithValue(
        err?.response?.data ||
          err?.message ||
          "Ошибка при удалении выходного дня"
      );
    }
  }
);

// Пакетная загрузка выходных для салона (несколько uid, PRIVATE + JWT)
export const fetchSalonDayOffs = createAsyncThunk(
  "dayOff/fetchSalonDayOffs",
  async (uids = [], { rejectWithValue }) => {
    try {
      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          try {
            const res = await apiRequest("GET", `/day-off/${uid}`);
            const items = (res?.data || []).map((x) => ({ ...x, uid }));
            return { uid, items };
          } catch (err) {
            const status = err?.response?.status;

            // 404 — нет записей → возвращаем пустой массив
            if (status === 404) {
              return { uid, items: [] };
            }

            if (status === 401 || status === 403) {
              try {
                await clearJwtToken();
              } catch {}
            }

            throw err;
          }
        })
      );

      return rows; // [{ uid, items: DayOff[] }]
    } catch {
      return rejectWithValue("Ошибка при салонной загрузке выходных дней");
    }
  }
);

// === ПУБЛИЧНО: выходные для одного uid (без headers/JWT) ===
export const fetchPublicDayOffs = createAsyncThunk(
  "dayOff/fetchPublicDayOffs",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = getServerUrl().replace(/\/$/, "");
      const res = await axios.get(`${base}/public/${uid}/day-offs`, {
        validateStatus: () => true, // сами обработаем 404
      });

      if (!res) throw new Error("Нет ответа от сервера.");
      if (res.status === 404) return []; // нет записей — это ок
      if (res.status !== 200) throw new Error(`Ошибка сервера: ${res.status}`);

      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ||
          err?.message ||
          "Ошибка при публичной загрузке выходных"
      );
    }
  }
);

// === ПУБЛИЧНО: пакетная загрузка выходных для нескольких uid ===
export const fetchPublicSalonDayOffs = createAsyncThunk(
  "dayOff/fetchPublicSalonDayOffs",
  async (uids = [], { rejectWithValue }) => {
    try {
      const base = getServerUrl().replace(/\/$/, "");
      const rows = await Promise.all(
        (uids || []).map(async (uid) => {
          const res = await axios.get(`${base}/public/${uid}/day-offs`, {
            validateStatus: () => true,
          });
          if (res.status === 404) return { uid, items: [] };
          if (res.status !== 200) throw new Error(`HTTP ${res.status}`);
          const items = (res.data || []).map((x) => ({ ...x, uid }));
          return { uid, items };
        })
      );
      return rows; // [{ uid, items }]
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ||
          err?.message ||
          "Ошибка при публичной салонной загрузке выходных"
      );
    }
  }
);

/* ========================= State ========================= */

const initialState = {
  // совместимость/простые экраны
  dayOffs: [],
  status: "idle",
  error: null,

  // расширенный режим для мульти-uid
  byUid: {},        // { [uid]: DayOff[] }
  statusByUid: {},  // { [uid]: "idle"|"loading"|"succeeded"|"failed" }
  errorByUid: {},   // { [uid]: string|null }
};

/* ========================= Slice ========================= */

const dayOffSlice = createSlice({
  name: "dayOff",
  initialState,
  reducers: {
    // Оптимистично добавить локально
    addDayOffLocal: (state, action) => {
      const item = action.payload;
      if (!item) return;
      state.dayOffs.push(item);
      const uid = String(item.uid || item._fetchedForUid || "");
      if (uid) {
        if (!state.byUid[uid]) state.byUid[uid] = [];
        state.byUid[uid].push(item);
      }
    },
    // Оптимистично удалить локально
    removeDayOffLocal: (state, action) => {
      const { uid, dateOfAppointment } = action.payload || {};
      if (!dateOfAppointment) return;
      state.dayOffs = state.dayOffs.filter(
        (d) => d.dateOfAppointment !== dateOfAppointment
      );
      if (uid != null) {
        const key = String(uid);
        state.byUid[key] = (state.byUid[key] || []).filter(
          (d) => d.dateOfAppointment !== dateOfAppointment
        );
      } else {
        // если uid не передали — удалить из всех
        Object.keys(state.byUid).forEach((k) => {
          state.byUid[k] = (state.byUid[k] || []).filter(
            (d) => d.dateOfAppointment !== dateOfAppointment
          );
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- addDayOff ---------- */
      .addCase(addDayOff.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addDayOff.fulfilled, (state, action) => {
        state.status = "succeeded";
        const row = action.payload;
        state.dayOffs.push(row);
        const uid = String(row?.uid ?? row?._fetchedForUid ?? "");
        if (uid) {
          if (!state.byUid[uid]) state.byUid[uid] = [];
          state.byUid[uid].push(row);
        }
      })
      .addCase(addDayOff.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "addDayOff failed";
      })

      /* ---------- fetchDayOffs (per uid, PRIVATE) ---------- */
      .addCase(fetchDayOffs.pending, (state, action) => {
        state.status = "loading";
        const uid = String(action.meta.arg || "");
        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(fetchDayOffs.fulfilled, (state, action) => {
        state.status = "succeeded";
        const uid = String(action.meta.arg);
        const arr = Array.isArray(action.payload)
          ? action.payload.map((x) => ({ ...x, _fetchedForUid: uid }))
          : [];
        state.dayOffs = arr;     // опционально держим плоский список
        state.byUid[uid] = arr;  // основное хранилище по uid
        state.statusByUid[uid] = "succeeded";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchDayOffs.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchDayOffs failed";
        const uid = String(action.meta?.arg || "");
        if (uid) {
          state.byUid[uid] = [];
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      /* ---------- deleteDayOff ---------- */
      .addCase(deleteDayOff.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteDayOff.fulfilled, (state, action) => {
        state.status = "succeeded";
        const date = action.payload;
        state.dayOffs = state.dayOffs.filter(
          (d) => d.dateOfAppointment !== date
        );
        Object.keys(state.byUid).forEach((uid) => {
          state.byUid[uid] = (state.byUid[uid] || []).filter(
            (d) => d.dateOfAppointment !== date
          );
        });
      })
      .addCase(deleteDayOff.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "deleteDayOff failed";
      })

      /* ---------- fetchSalonDayOffs (PRIVATE batch) ---------- */
      .addCase(fetchSalonDayOffs.pending, (state, action) => {
        const uids = action.meta?.arg || [];
        (uids || []).forEach((uid) => {
          state.statusByUid[String(uid)] = "loading";
        });
      })
      .addCase(fetchSalonDayOffs.fulfilled, (state, action) => {
        const rows = action.payload || [];
        rows.forEach(({ uid, items }) => {
          const key = String(uid);
          state.byUid[key] = (items || []).map((x) => ({
            ...x,
            uid: key,
          }));
          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;
        });
      })
      .addCase(fetchSalonDayOffs.rejected, (state, action) => {
        const uids = action.meta?.arg || [];
        (uids || []).forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "failed";
          state.errorByUid[key] =
            action.payload || "fetchSalonDayOffs failed";
        });
      })

      /* ---------- PUBLIC: fetchPublicDayOffs (per uid) ---------- */
      .addCase(fetchPublicDayOffs.pending, (state, action) => {
        state.status = "loading";
        const uid = String(action.meta.arg || "");
        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(fetchPublicDayOffs.fulfilled, (state, action) => {
        state.status = "succeeded";
        const uid = String(action.meta.arg);
        const arr = Array.isArray(action.payload)
          ? action.payload.map((x) => ({ ...x, _fetchedForUid: uid }))
          : [];
        state.dayOffs = arr;     // плоский список (как и у приватного fetchDayOffs)
        state.byUid[uid] = arr;  // кэш по uid — тот же ключ
        state.statusByUid[uid] = "succeeded";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchPublicDayOffs.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicDayOffs failed";
        const uid = String(action.meta?.arg || "");
        if (uid) {
          state.byUid[uid] = [];
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = state.error;
        }
      })

      /* ---------- PUBLIC: fetchPublicSalonDayOffs (batch) ---------- */
      .addCase(fetchPublicSalonDayOffs.pending, (state, action) => {
        const uids = action.meta?.arg || [];
        (uids || []).forEach((uid) => {
          state.statusByUid[String(uid)] = "loading";
          state.errorByUid[String(uid)] = null;
        });
      })
      .addCase(fetchPublicSalonDayOffs.fulfilled, (state, action) => {
        const rows = action.payload || [];
        rows.forEach(({ uid, items }) => {
          const key = String(uid);
          state.byUid[key] = (items || []).map((x) => ({
            ...x,
            uid: key,
          }));
          state.statusByUid[key] = "succeeded";
          state.errorByUid[key] = null;
        });
      })
      .addCase(fetchPublicSalonDayOffs.rejected, (state, action) => {
        const uids = action.meta?.arg || [];
        const msg =
          action.payload ||
          action.error?.message ||
          "fetchPublicSalonDayOffs failed";
        (uids || []).forEach((uid) => {
          const key = String(uid);
          state.statusByUid[key] = "failed";
          state.errorByUid[key] = msg;
        });
      })

      /* ---------- reset ---------- */
      .addCase(resetStore, () => initialState);
  },
});

export const { addDayOffLocal, removeDayOffLocal } = dayOffSlice.actions;

/* ========================= Selectors ========================= */

// Для конкретного мастера
export const selectDayOffsForUid = (s, uid) =>
  s.dayOff?.byUid?.[String(uid)] || [];

// Собрать выходные по множеству uid (салон)
export const selectDayOffsForSalon = createSelector(
  [(s) => s.dayOff?.byUid, (s, uids) => uids],
  (byUid, uids = []) => {
    if (!byUid || !uids.length) return [];
    return uids.flatMap((uid) => byUid[String(uid)] || []);
  }
);

// Все выходные за конкретную дату (по всем uid)
export const selectDayOffsByDate = createSelector(
  [(s) => s.dayOff?.byUid, (s, date) => date],
  (byUid, date) => {
    if (!byUid || !date) return [];
    return Object.keys(byUid).flatMap((uid) =>
      (byUid[uid] || []).filter(
        (d) => d.dateOfAppointment === date
      )
    );
  }
);

// Быстрая проверка: день является выходным у конкретного uid?
export const selectIsDayOff = (s, uid, date) =>
  !!(s.dayOff?.byUid?.[String(uid)] || []).find(
    (d) => d.dateOfAppointment === date
  );

export default dayOffSlice.reducer;
